/**
 * Os cinco cards do disco (F-GC.6): o que o dono acompanha para saber se o alvo
 * de 80 % está certo.
 *
 * Tudo chega pronto do servidor — o percentual com a régua do GC, as datas da
 * última passada, os contadores das 24 h. Aqui só se formata: nenhuma conta,
 * nenhuma comparação com o alvo e nenhuma cor que mude sozinha (sem alertas
 * automáticos, decisão do dono).
 *
 * As datas saem em UTC de propósito: «retendo desde» tem de ser o mesmo dia que
 * a linha `viewer gc pass complete … retaining_since=` do log, que é o que se
 * confere contra a tela.
 */
import type { InsightOverview } from "./api";
import { formatBytes } from "./format.ts";

export interface GcIndicatorCard {
  key: "disk" | "retention" | "expired" | "exists_false" | "cold_opens";
  label: string;
  value: string;
  subLabel: string;
}

export type GcIndicatorSource = Pick<
  InsightOverview,
  | "disk_total_bytes"
  | "disk_used_percent"
  | "disk_target_percent"
  | "retained_since"
  | "oldest_pyramid_ready_at"
  | "gc_pass_at"
  | "expired_assets_24h"
  | "expired_bytes_24h"
  | "exists_false_24h"
  | "cold_opens_24h"
>;

const percentFormatter = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const targetFormatter = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 });
const integerFormatter = new Intl.NumberFormat("pt-BR");

function utcParts(iso: string): Record<string, string> | null {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return null;
  const parts = new Intl.DateTimeFormat("pt-BR", {
    timeZone: "UTC",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(instant);
  return Object.fromEntries(parts.map((part) => [part.type, part.value]));
}

/** `03/09/2026`, no dia UTC. */
export function formatUtcDate(iso: string | null | undefined): string {
  const parts = iso ? utcParts(iso) : null;
  return parts ? `${parts.day}/${parts.month}/${parts.year}` : "—";
}

/** `04/10 19:00 UTC`. */
export function formatUtcDayTime(iso: string | null | undefined): string {
  const parts = iso ? utcParts(iso) : null;
  return parts ? `${parts.day}/${parts.month} ${parts.hour}:${parts.minute} UTC` : "—";
}

function diskCard(o: GcIndicatorSource): GcIndicatorCard {
  const alvo =
    o.disk_target_percent == null ? null : `alvo ${targetFormatter.format(o.disk_target_percent)} %`;
  if (o.disk_used_percent == null || o.disk_total_bytes == null) {
    return {
      key: "disk",
      label: "Disco",
      value: "—",
      subLabel: alvo ? `Ainda não medido · ${alvo}` : "Ainda não medido",
    };
  }
  return {
    key: "disk",
    label: "Disco",
    value: `${percentFormatter.format(o.disk_used_percent)} %`,
    subLabel: [`de ${formatBytes(o.disk_total_bytes)}`, alvo].filter(Boolean).join(" · "),
  };
}

function retentionCard(o: GcIndicatorSource): GcIndicatorCard {
  if (o.gc_pass_at == null) {
    return {
      key: "retention",
      label: "Retendo arquivos usados desde",
      value: formatUtcDate(o.retained_since),
      subLabel: "A limpeza ainda não publicou uma passada.",
    };
  }
  const piramide =
    o.oldest_pyramid_ready_at == null
      ? null
      : `Pirâmide mais antiga no disco: ${formatUtcDate(o.oldest_pyramid_ready_at)}`;
  return {
    key: "retention",
    label: "Retendo arquivos usados desde",
    value: formatUtcDate(o.retained_since),
    subLabel: [piramide, `limpeza de ${formatUtcDayTime(o.gc_pass_at)}`]
      .filter(Boolean)
      .join(" · "),
  };
}

export function gcIndicatorCards(o: GcIndicatorSource): GcIndicatorCard[] {
  return [
    diskCard(o),
    retentionCard(o),
    {
      key: "expired",
      label: "Expirados nas últimas 24 h",
      value: integerFormatter.format(o.expired_assets_24h),
      subLabel: `${formatBytes(o.expired_bytes_24h)} liberados. Ficam listados; a plataforma reenvia ao abrir.`,
    },
    {
      key: "exists_false",
      label: "Reenvios pedidos à plataforma",
      value: integerFormatter.format(o.exists_false_24h),
      subLabel: "Consultas que responderam exists=false nas últimas 24 h.",
    },
    {
      key: "cold_opens",
      label: "Aberturas a frio",
      value: integerFormatter.format(o.cold_opens_24h),
      subLabel: "Arquivos abertos sem a imagem no disco, refeitos na hora, nas últimas 24 h.",
    },
  ];
}
